import {ActionIcon, Anchor, Group, Paper, Select, Stack, Table, Text, Title} from '@mantine/core'
import {IconDownload, IconTrash} from '@tabler/icons-react'
import {useState} from 'react'

import {formatBusinessTimestamp} from 'src/api/business-time'
import {useCustomerDocuments} from 'src/customers/documents/use-documents'

import type {JobDocument} from './documents/schemas'
import {
    useAssignDocumentToJob,
    useJobDocuments,
    useUnassignDocumentFromJob,
} from './documents/use-job-documents'
import {getJobDocumentDownloadUrl} from './documents/use-job-documents'
import {useEditRights} from './edit-rights-context'

type JobDocumentsSectionProps = {
    jobId: string
    customerId: string
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

function formatDate(date: Date): string {
    return formatBusinessTimestamp(date, 'MMM d, yyyy')
}

export function JobDocumentsSection({jobId, customerId}: JobDocumentsSectionProps) {
    const {canEdit} = useEditRights()
    const {data: jobDocuments = [], isLoading: jobLoading} = useJobDocuments(jobId)
    const {data: customerDocuments = [], isLoading: customerLoading} =
        useCustomerDocuments(customerId)
    const assignDocument = useAssignDocumentToJob(jobId)
    const unassignDocument = useUnassignDocumentFromJob(jobId)

    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const saving = assignDocument.isPending || unassignDocument.isPending

    const assignedDocumentIds = new Set(jobDocuments.map((d) => d.id))
    const availableDocuments = customerDocuments.filter((d) => !assignedDocumentIds.has(d.id))

    async function handleAssign(documentId: string | null) {
        if (!documentId) {
            return
        }

        setErrorMessage(null)
        try {
            await assignDocument.mutateAsync({document_id: documentId})
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to assign document')
        }
    }

    async function handleUnassign(document: JobDocument) {
        if (!window.confirm(`Unassign "${document.name}" from this job?`)) {
            return
        }

        setErrorMessage(null)
        try {
            await unassignDocument.mutateAsync(document.id)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to unassign document')
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3}>Documents</Title>
                </Group>

                {errorMessage ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                {customerLoading || jobLoading ? (
                    <Text size="sm" c="dimmed">
                        Loading...
                    </Text>
                ) : (
                    <>
                        {canEdit ? (
                            <Select
                                label="Assign document"
                                placeholder="Select a document to assign"
                                data={availableDocuments.map((doc) => ({
                                    value: doc.id,
                                    label: doc.name,
                                }))}
                                onChange={(value) => void handleAssign(value)}
                                searchable
                                nothingFoundMessage="No documents available"
                                disabled={saving}
                            />
                        ) : null}

                        {jobDocuments.length === 0 ? (
                            <Text size="sm" c="dimmed">
                                No documents assigned to this job yet.
                            </Text>
                        ) : (
                            <Table>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Name</Table.Th>
                                        <Table.Th>File</Table.Th>
                                        <Table.Th>Size</Table.Th>
                                        <Table.Th>Created</Table.Th>
                                        <Table.Th>Actions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {jobDocuments.map((document) => (
                                        <Table.Tr key={document.id}>
                                            <Table.Td>{document.name}</Table.Td>
                                            <Table.Td>{document.original_filename}</Table.Td>
                                            <Table.Td>{formatBytes(document.size_bytes)}</Table.Td>
                                            <Table.Td>{formatDate(document.created_at)}</Table.Td>
                                            <Table.Td>
                                                <Group gap="xs">
                                                    <Anchor
                                                        href={getJobDocumentDownloadUrl(
                                                            jobId,
                                                            document.id,
                                                        )}
                                                        download
                                                        aria-label={`Download ${document.name}`}
                                                    >
                                                        <IconDownload size={16} />
                                                    </Anchor>
                                                    {canEdit ? (
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="default"
                                                            color="red"
                                                            aria-label={`Unassign ${document.name}`}
                                                            onClick={() =>
                                                                void handleUnassign(document)
                                                            }
                                                            disabled={saving}
                                                        >
                                                            <IconTrash size={16} />
                                                        </ActionIcon>
                                                    ) : null}
                                                </Group>
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        )}
                    </>
                )}
            </Stack>
        </Paper>
    )
}
